import { axiosInstance } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";

export interface Course {
  _id: string;
  title: {
    en: string;
    am: string;
  };
  courseCode: string;
  courseDescription: {
    en: string;
    am: string;
  };
  courseLevel: "Beginner" | "Intermediate" | "Advanced";
  thumbnail: string;
  price: number;
  slug: string;
  createdAt: string;
  isActive: boolean;
}

export const useCourses = () => {
  return useQuery<Course[], unknown>({
    queryKey: ["courses"],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/api/v1/courses");
      return data?.data?.documents ?? [];
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors to prevent infinite loops
      if (error?.response?.status === 401) {
        console.log("Courses API 401 error, not retrying");
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: 1000,
    // Use global defaults for refetching
  });
};

export const useCourse = (courseId: string) => {
  return useQuery({
    queryKey: ["course", courseId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/api/v1/courses/${courseId}`);
      return data?.data;
    },
    enabled: !!courseId,
  });
};


export const useCourseStatus = (courseId: string) => {
  return useQuery({
    queryKey: ["courseStatus", courseId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/api/v1/companies/course-status/${courseId}`
      );
      return data?.data;
    },
    enabled: !!courseId,
    refetchInterval: 10000, // Poll every 10 seconds for real-time updates
  });
};

export const useCourseTopPerformers = (courseId: string) => {
  return useQuery({
    queryKey: ["courseTopPerformers", courseId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/api/v1/companies/course-top-performers/${courseId}`
      );
      return data?.data;
    },
    enabled: !!courseId,
    refetchInterval: 10000, // Poll every 10 seconds for real-time updates
  });
};

export interface DepartmentComparison {
  departmentName: string;
  totalScore: number;
  employeeCount: number;
}

export const useDepartmentComparison = (enabled: boolean = true, userRole?: string) => {
  return useQuery<DepartmentComparison[], unknown>({
    queryKey: ["departmentComparison", userRole],
    queryFn: async () => {
      // Use different endpoints based on user role
      const endpoint = userRole === "department_admin" 
        ? "/api/v1/departments/comparison" 
        : "/api/v1/companies/department-comparision";
      
      const { data } = await axiosInstance.get(endpoint);
      return data?.data ?? [];
    },
    enabled: enabled && (userRole === "company_admin" || userRole === "department_admin"),
  });
};

export const useMonthlyChartData = () => {
  return useQuery({
    queryKey: ["monthlyChartData"],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        "/api/v1/companies/get-monthly-completed-courses"
      );
      return data?.data ?? {}; 
    },
  });
};
